import { apiClient as api } from "./httpClient";

/* Aba Financeiro do paciente — tela de caixa (API api/patient-financial). */

export interface FinancialSession {
  id: string;
  date: string;
  /** fim da sessão (ISO, relógio da entidade); nulo sem hora final */
  endDate: string | null;
  serviceTypeName: string | null;
  professionalId: string | null;
  professionalName: string | null;
  presence: string | null;
  /** presença com significado "atendido" (RN-114d) */
  attended?: boolean;
  unitValue: number;
  patientDiscount: number;
  netValue: number;
  paid: boolean;
  paidValue: number | null;
  paymentDiscount: number | null;
  paymentId: string | null;
  paymentDate: string | null;
  byInjunction: boolean;
  /** anterior à data de corte financeira (RN-543h): histórico, sem ações */
  financiallyLocked?: boolean;
  /** pacote (RN-228): tamanho, posição na série e pai; nulos quando não é pacote */
  packageSize?: number | null;
  packagePosition?: number | null;
  packageParentId?: string | null;
  /** serviço de pacote sem série (herança do legado) */
  packageUnlinked?: boolean;
  /** modo de cobrança do pacote (RN-227) */
  packageChargeMode?: 'total' | 'attended' | null;
  /** status exibido na agenda (presença ou confirmação) e a cor do catálogo; nulo nos defaults */
  statusDisplay?: string | null;
  statusColor?: string | null;
}

export interface FinancialPayment {
  id: string;
  date: string;
  originalValue: number;
  total: number;
  method1Id: string | null;
  method1Name: string | null;
  value1: number | null;
  method2Id: string | null;
  method2Name: string | null;
  value2: number | null;
  sessions: number;
  reverted: boolean;
  revertedJustify: string | null;
  comments: string | null;
  /** só a observação livre */
  notes: string | null;
  invoiceUrl: string | null;
  byInjunction: boolean;
}

export interface FinancialPatientDiscount {
  id: string;
  value: number;
  serviceTypeId: string | null;
  serviceTypeName: string | null;
  professionalTypeId: string | null;
  professionalTypeName: string | null;
  professionalIds: string[];
  professionalNames: string | null;
  teamId: string | null;
  teamName: string | null;
  from: string | null;
  to: string | null;
  comments: string | null;
  active: boolean;
}

export interface PatientPeriodicity { id: string; period: string; active: boolean; createdAt: string; }
export interface PatientHealthPlanLink {
  id: string; planId: string | null; name: string; refundable: boolean; active: boolean; createdAt: string | null;
  // Nulos quando o perfil não tem o grupo "convênio" dos dados sensíveis (RN-112q)
  cardNumber: string | null; cardValidUntil: string | null; isHolder: boolean | null; holderName: string | null;
}
export interface OptionItem { id: string; name: string; }
/** Plano escolhível no vínculo, com o convênio para a seleção em cascata. */
export interface HealthPlanPlanOption extends OptionItem { operatorId: string; }
export const PERIODICITY_OPTIONS = ['Semanal', 'Quinzenal', 'Mensal', 'Diário'];

export interface SavePatientDiscountPayload {
  id: string | null;
  patientId: string;
  discount: number;
  serviceTypeId: string | null;
  professionalTypeId: string | null;
  professionalIds: string[];
  teamId: string | null;
  initialDate: string | null;
  finalDate: string | null;
  comments: string | null;
}

export interface SetPatientHealthPlanPayload {
  patientId: string;
  /** Vínculo em edição; nulo = vínculo novo. */
  linkId: string | null;
  healthPlanId: string | null;
  newOperatorId: string | null;
  newPlanName: string | null;
  refundable: boolean;
  cardNumber: string | null;
  cardValidUntil: string | null;
  isHolder: boolean | null;
  holderName: string | null;
}

export interface GeneralDiscountOption { id: string; title: string; percent: number; }
export interface PaymentMethodOption { id: string; name: string; }

export interface PatientFinancial {
  sessions: FinancialSession[];
  professionals: { id: string; name: string }[];
  payments: FinancialPayment[];
  patientDiscounts: FinancialPatientDiscount[];
  generalDiscounts: GeneralDiscountOption[];
  paymentMethods: PaymentMethodOption[];
  periodicity: string | null;
  healthPlan: { name: string; refundable: boolean } | null;
  periodicityHistory: PatientPeriodicity[];
  healthPlans: PatientHealthPlanLink[];
  healthPlanOptions: HealthPlanPlanOption[];
  healthPlanOperatorOptions: OptionItem[];
  healthPlanDataVisible: boolean;
  professionalTypeOptions: OptionItem[];
  contracts: { type: string; subscriptionDate: string | null; expirationDate: string | null }[];
  injunction: { id: string; title: string; from: string | null; to: string | null; activeNow: boolean } | null;
  pendingTotal: number;
  pendingCount: number;
  pendingSince: string | null;
  /** data de corte financeira da entidade (aaaa-mm-dd), nula sem corte */
  financialCutoffDate?: string | null;
  /** quanto do pendente é de sessões de pacote (RN-228) */
  pendingPackageTotal?: number;
}

export interface PayPatientSessionsPayload {
  patientId: string;
  scheduleIds: string[];
  generalDiscountId: string | null;
  manualAdjust: number;
  manualJustification: string | null;
  method1Id: string | null;
  value1: number | null;
  method2Id: string | null;
  value2: number | null;
  notes: string | null;
  byInjunction: boolean;
  invoiceUrl: string | null;
  /** Data em que o pagamento aconteceu (AAAA-MM-DD); nula = hoje (RN-543j). Nunca futura. */
  paymentDate: string | null;
}

export interface PaymentReceiptItem {
  scheduleId: string;
  date: string;
  serviceTypeName: string | null;
  professionalName: string | null;
  professionalTypeName: string | null;
  unitValue: number;
  /** desconto programado (gravado no agendamento) */
  programmedDiscount: number;
  /** desconto/acréscimo do ato do pagamento; negativo = acréscimo */
  discount: number;
  finalValue: number;
}

export interface PaymentReceipt {
  paymentId: string;
  date: string;
  patientId: string;
  patientName: string;
  payerName: string | null;
  payerDocument: string | null;
  items: PaymentReceiptItem[];
  originalTotal: number;
  total: number;
  method1Name: string | null;
  value1: number | null;
  method2Name: string | null;
  value2: number | null;
  comments: string | null;
  invoiceUrl: string | null;
  issuedBy: string | null;
  reverted: boolean;
  byInjunction: boolean;
}

/**
 * Paciente para abrir o caixa (RN-218g). A busca vive sob a permissão da tela
 * (`patient-financial`) e não em /patients: quem opera o caixa não recebe a Lista de pacientes, e
 * pela rota do cadastro o campo abria vazio com 403 para o perfil Financeiro.
 */
export interface FinancialPatientOption {
  id: string;
  patientName: string;
  patientDocument: string | null;
  patientBirthDate: string | null;
  responsibleName1: string | null;
  responsibleName2: string | null;
  active: boolean;
}

export const patientFinancialService = {
  searchPatients: async (search: string, limit = 20): Promise<FinancialPatientOption[]> => {
    const response = await api.get("/patient-financial/patients", { params: { search, limit } });
    return response.data;
  },

  getPatient: async (patientId: string): Promise<FinancialPatientOption> => {
    const response = await api.get(`/patient-financial/patients/${patientId}`);
    return response.data;
  },

  getByPatient: async (patientId: string): Promise<PatientFinancial> => {
    const response = await api.get(`/patient-financial/by-patient/${patientId}`);
    return response.data;
  },

  /** Extrato em Excel com o mesmo filtro da tela (período, profissional, situação) */
  exportXlsx: async (patientId: string, filters: { from?: string; to?: string; professionalId?: string; situation?: string }): Promise<Blob> => {
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) q.set(k, v); });
    const response = await api.get(`/patient-financial/by-patient/${patientId}/xlsx?${q.toString()}`, { responseType: 'blob', timeout: 60000 });
    return response.data;
  },

  pay: async (payload: PayPatientSessionsPayload): Promise<PaymentReceipt> => {
    const response = await api.post("/patient-financial/payments", payload, { timeout: 60000 });
    return response.data;
  },

  updateMethods: async (paymentId: string, payload: { method1Id: string | null; value1: number | null; method2Id: string | null; value2: number | null; notes: string | null; invoiceUrl: string | null; paymentDate: string | null }): Promise<void> => {
    await api.put(`/patient-financial/payments/${paymentId}/methods`, payload, { timeout: 60000 });
  },

  revert: async (paymentId: string, justification: string): Promise<void> => {
    await api.post(`/patient-financial/payments/${paymentId}/revert`, { justification }, { timeout: 60000 });
  },

  saveDiscount: async (payload: SavePatientDiscountPayload): Promise<string> => {
    const response = await api.post("/patient-financial/discounts", payload, { timeout: 60000 });
    return response.data.id;
  },

  setDiscountActive: async (id: string, active: boolean): Promise<void> => {
    await api.post(`/patient-financial/discounts/${id}/active`, { active }, { timeout: 60000 });
  },

  setPeriodicity: async (patientId: string, period: string | null): Promise<void> => {
    await api.put("/patient-financial/periodicity", { patientId, period }, { timeout: 60000 });
  },

  setHealthPlan: async (payload: SetPatientHealthPlanPayload): Promise<string> => {
    const response = await api.post("/patient-financial/health-plans", payload, { timeout: 60000 });
    return response.data.id;
  },

  setHealthPlanActive: async (id: string, active: boolean): Promise<void> => {
    await api.post(`/patient-financial/health-plans/${id}/active`, { active }, { timeout: 60000 });
  },

  getReceipt: async (paymentId: string): Promise<PaymentReceipt> => {
    const response = await api.get(`/patient-financial/payments/${paymentId}/receipt`);
    return response.data;
  },
};
